(function () {
  const config = window.AnandDiscountConfig || {};
  const DISCOUNT_CODE = config.discountCode || '';
  const REVEAL_DELAY_MS = Number(config.revealDelayMs) || 1000;
  const STORAGE_KEY = config.sessionStorageKey || 'anand-active-discount-code';
  const CACHE_KEY = config.discountCacheKey || 'anand-discount-cache-v1';
  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pendingVariantResolutions = new Map();

  function parseIntSafe(value) {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function getJsonCartEndpoint(url) {
    return url.endsWith('.js') ? url : `${url}.js`;
  }

  function formatMoney(cents) {
    return `Rs. ${new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format((cents || 0) / 100)}`;
  }

  function getSessionValue(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function setSessionValue(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      // no-op
    }
  }

  async function applyDiscountCodeOnce() {
    if (!DISCOUNT_CODE) return;
    if (getSessionValue(STORAGE_KEY) === DISCOUNT_CODE) return;

    const redirectTarget = `${window.location.pathname}${window.location.search}`;
    const discountUrl = `/discount/${encodeURIComponent(DISCOUNT_CODE)}?redirect=${encodeURIComponent(redirectTarget)}`;

    try {
      await fetch(discountUrl, {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });
      setSessionValue(STORAGE_KEY, DISCOUNT_CODE);
    } catch (error) {
      console.warn('Unable to pre-apply discount code.', error);
    }
  }

  async function fetchCartState() {
    const response = await fetch(`${window.routes.cart_url}.js`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error(`Cart request failed with ${response.status}`);
    }

    return response.json();
  }

  async function postCartMutation(url, payload) {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Cart mutation failed with ${response.status}`);
    }

    return response.json();
  }

  function getDiscountCache() {
    try {
      return JSON.parse(window.sessionStorage.getItem(CACHE_KEY) || '{}');
    } catch (error) {
      return {};
    }
  }

  function setDiscountCache(cache) {
    try {
      window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      // no-op
    }
  }

  function getCachedDiscountState(variantId) {
    if (!DISCOUNT_CODE || !variantId) return null;

    const cache = getDiscountCache();
    return cache[`${DISCOUNT_CODE}:${variantId}`] || null;
  }

  function setCachedDiscountState(variantId, state) {
    if (!DISCOUNT_CODE || !variantId) return;

    const cache = getDiscountCache();
    cache[`${DISCOUNT_CODE}:${variantId}`] = {
      hasDiscount: Boolean(state.hasDiscount),
      originalUnitPrice: parseIntSafe(state.originalUnitPrice),
      finalUnitPrice: parseIntSafe(state.finalUnitPrice),
      source: state.source || 'cart',
    };
    setDiscountCache(cache);
  }

  function findCartLineByVariant(cartState, variantId) {
    return (cartState.items || []).find((item) => parseIntSafe(item.variant_id) === variantId) || null;
  }

  function buildDiscountStateFromLine(line, fallbackUnitPrice) {
    const lineOriginalPrice = parseIntSafe(line?.original_price);
    const lineFinalPrice = parseIntSafe(line?.final_price);
    const originalUnitPrice = lineOriginalPrice || fallbackUnitPrice;
    const finalUnitPrice = lineFinalPrice || fallbackUnitPrice;

    return {
      hasDiscount: originalUnitPrice > finalUnitPrice,
      originalUnitPrice,
      finalUnitPrice,
      source: line ? 'cart' : 'base',
    };
  }

  async function resolveDiscountStateForVariant(variantId, fallbackUnitPrice) {
    if (!DISCOUNT_CODE || !variantId) {
      return buildDiscountStateFromLine(null, fallbackUnitPrice);
    }

    const cachedState = getCachedDiscountState(variantId);
    if (cachedState) {
      return cachedState;
    }

    const cacheKey = String(variantId);
    if (pendingVariantResolutions.has(cacheKey)) {
      return pendingVariantResolutions.get(cacheKey);
    }

    const pendingResolution = (async () => {
      await applyDiscountCodeOnce();

      const cartBeforeProbe = await fetchCartState();
      const existingLine = findCartLineByVariant(cartBeforeProbe, variantId);
      if (existingLine) {
        const existingState = buildDiscountStateFromLine(existingLine, fallbackUnitPrice);
        setCachedDiscountState(variantId, existingState);
        return existingState;
      }

      let probedLine = null;

      try {
        await postCartMutation(getJsonCartEndpoint(window.routes.cart_add_url), {
          items: [
            {
              id: variantId,
              quantity: 1,
            },
          ],
        });

        const cartAfterProbe = await fetchCartState();
        probedLine = findCartLineByVariant(cartAfterProbe, variantId);
        const probedState = buildDiscountStateFromLine(probedLine, fallbackUnitPrice);
        setCachedDiscountState(variantId, probedState);
        return probedState;
      } finally {
        if (probedLine?.key) {
          try {
            await postCartMutation(getJsonCartEndpoint(window.routes.cart_change_url), {
              id: probedLine.key,
              quantity: 0,
            });
          } catch (error) {
            console.warn('Unable to revert discount probe line item.', error);
          }
        }
      }
    })();

    pendingVariantResolutions.set(cacheKey, pendingResolution);

    try {
      return await pendingResolution;
    } finally {
      pendingVariantResolutions.delete(cacheKey);
    }
  }

  function animateMoney(element, startCents, endCents, duration) {
    if (!element) return;
    if (reduceMotionQuery.matches || duration <= 0 || startCents === endCents) {
      element.textContent = formatMoney(endCents);
      return;
    }

    const startTime = performance.now();

    const step = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(startCents + (endCents - startCents) * eased);
      element.textContent = formatMoney(current);

      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }

  function createCelebrationBurst(target) {
    if (!target) return;

    if (reduceMotionQuery.matches) {
      target.classList.add('is-highlighted');
      window.setTimeout(() => target.classList.remove('is-highlighted'), 500);
      return;
    }

    const burst = document.createElement('div');
    burst.className = 'anand-celebration-burst';

    const isCompact = Boolean(target.closest('.cart-drawer__footer-totals'));
    const totalParticles = isCompact ? 28 : 50;

    const colors = [
      '#FFD700', '#FF6B6B', '#4ECDC4', '#c5a880',
      '#FF9F43', '#A55EEA', '#2ED573', '#FF4757',
      '#1E90FF', '#eabf82', '#FF69B4', '#00D2D3'
    ];

    // Shape types: ribbon, circle, star, spark
    const shapes = ['ribbon', 'ribbon', 'ribbon', 'circle', 'star', 'spark'];

    for (let i = 0; i < totalParticles; i++) {
      const particle = document.createElement('span');
      const shape = shapes[i % shapes.length];
      particle.className = `anand-confetti anand-confetti--${shape}`;

      const color = colors[i % colors.length];
      particle.style.setProperty('--confetti-color', color);

      // Random origin near center
      const originX = 45 + Math.random() * 10;
      const originY = 40 + Math.random() * 20;
      particle.style.left = `${originX}%`;
      particle.style.top = `${originY}%`;

      // Explosion direction — spread wide like a party popper
      const angle = (Math.PI * 2 * i) / totalParticles + (Math.random() - 0.5) * 0.8;
      const velocity = isCompact ? 60 + Math.random() * 100 : 80 + Math.random() * 160;
      const dx = Math.cos(angle) * velocity;
      const dy = Math.sin(angle) * velocity - 40; // upward bias

      particle.style.setProperty('--confetti-x', `${dx}px`);
      particle.style.setProperty('--confetti-y', `${dy}px`);
      particle.style.setProperty('--confetti-gravity', `${80 + Math.random() * 60}px`);
      particle.style.setProperty('--confetti-spin', `${-720 + Math.random() * 1440}deg`);
      particle.style.setProperty('--confetti-flip', `${Math.random() * 360}deg`);
      particle.style.animationDelay = `${i * 15 + Math.random() * 80}ms`;
      particle.style.animationDuration = `${1.2 + Math.random() * 1}s`;

      burst.appendChild(particle);
    }

    target.appendChild(burst);
    window.setTimeout(() => burst.remove(), 3200);
  }

  class PDPDiscountReveal {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.section;
      this.runId = 0;
      this.currentState = null;

      if (!this.refreshDomReferences()) {
        return;
      }

      this.bindEvents();
      this.resolveDiscount();
    }

    bindEvents() {
      const refresh = () => {
        if (this.currentState) this.currentState.animate = false;
        window.setTimeout(() => this.renderCurrentState(), 0);
      };

      this.quantityInput?.addEventListener('input', refresh);
      this.quantityInput?.addEventListener('change', refresh);
      this.root.querySelector('.quantity__button[name="plus"]')?.addEventListener('click', refresh);
      this.root.querySelector('.quantity__button[name="minus"]')?.addEventListener('click', refresh);

      this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data.sectionId !== this.sectionId) return;
        this.resolveDiscount();
      });
    }

    destroy() {
      this.variantUnsubscriber?.();
    }

    refreshDomReferences() {
      this.priceContainer = this.root.querySelector(`#price-${this.sectionId}`);
      this.priceElement = this.priceContainer?.querySelector('.price');
      this.revealContainer = this.root.querySelector(`#DiscountReveal-${this.sectionId}`);
      this.quantityInput = this.root.querySelector('.quantity__input');
      this.variantInput = this.root.querySelector(`#product-form-${this.sectionId} [name="id"]`);
      this.revealPill = this.revealContainer?.querySelector('.anand-discount-reveal__pill');
      this.revealPillText = this.revealContainer?.querySelector('.anand-discount-reveal__pill-text');
      this.revealSummary = this.revealContainer?.querySelector('.anand-discount-reveal__summary');
      this.revealAmount = this.revealContainer?.querySelector('.anand-discount-reveal__amount');
      this.revealPercent = this.revealContainer?.querySelector('.anand-discount-reveal__percent');
      this.celebrationHost = this.revealContainer?.querySelector('.anand-discount-reveal__celebration');

      return Boolean(this.priceContainer && this.priceElement && this.revealContainer && this.variantInput);
    }

    getQuantity() {
      const quantity = parseIntSafe(this.quantityInput?.value);
      return quantity > 0 ? quantity : 1;
    }

    getVariantId() {
      return parseIntSafe(this.variantInput?.value);
    }

    getUnitPricesFromMarkup() {
      return {
        finalPrice: parseIntSafe(this.priceElement.dataset.basePriceCents),
        comparePrice: parseIntSafe(this.priceElement.dataset.comparePriceCents),
      };
    }

    buildFallbackState() {
      const { finalPrice } = this.getUnitPricesFromMarkup();

      return {
        hasDiscount: false,
        originalUnitPrice: finalPrice,
        finalUnitPrice: finalPrice,
        source: 'base',
      };
    }

    setDisplayState(isPending, hasDiscount) {
      this.priceContainer.classList.toggle('anand-discount-pending', isPending && hasDiscount);
      this.priceContainer.classList.toggle('anand-discount-revealed', !isPending && hasDiscount);
      this.priceElement.classList.toggle('price--on-sale', hasDiscount);
    }

    updatePriceMarkup(originalUnitPrice, finalUnitPrice, options) {
      const quantity = this.getQuantity();
      const totalOriginal = originalUnitPrice * quantity;
      const totalFinal = finalUnitPrice * quantity;
      const regularPrice = this.priceContainer.querySelector('.price__regular .price-item--regular');
      const salePrice = this.priceContainer.querySelector('.price__sale .price-item--sale');
      const comparePrice = this.priceContainer.querySelector('.price__sale s.price-item--regular');

      this.priceElement.dataset.basePrice = (finalUnitPrice / 100).toFixed(2);
      this.priceElement.dataset.comparePrice = options.hasDiscount ? (originalUnitPrice / 100).toFixed(2) : '0';
      this.priceElement.dataset.basePriceCents = String(finalUnitPrice);
      this.priceElement.dataset.comparePriceCents = options.hasDiscount ? String(originalUnitPrice) : '0';

      if (regularPrice) {
        regularPrice.textContent = formatMoney(options.isPending && options.hasDiscount ? totalOriginal : totalFinal);
      }

      if (salePrice) {
        if (options.animate) {
          animateMoney(salePrice, totalOriginal, totalFinal, 650);
        } else {
          salePrice.textContent = formatMoney(totalFinal);
        }
      }

      if (comparePrice) {
        comparePrice.textContent = formatMoney(totalOriginal);
      }
    }

    updateRevealMeta(state) {
      this.revealContainer.classList.toggle('is-reserving', Boolean(state.isPending || state.hasDiscount));

      if (!state.hasDiscount || state.isPending) {
        this.revealContainer.classList.remove('is-active');
        this.revealPill.hidden = true;
        this.revealSummary.hidden = true;
        return;
      }

      const savings = Math.max(state.originalUnitPrice - state.finalUnitPrice, 0) * this.getQuantity();
      const percent = state.originalUnitPrice > state.finalUnitPrice
        ? Math.round(((state.originalUnitPrice - state.finalUnitPrice) / state.originalUnitPrice) * 100)
        : 0;

      this.revealPillText.textContent = state.source === 'cart' && DISCOUNT_CODE
        ? `🎉 ${DISCOUNT_CODE} Unlocked!`
        : '🎉 Exclusive Offer Unlocked!';
      this.revealAmount.textContent = formatMoney(savings);
      this.revealPercent.textContent = percent > 0 ? `${percent}% off` : '';
      this.revealPill.hidden = false;
      this.revealSummary.hidden = false;

      // Force a reflow then add is-active so CSS entrance animations fire
      this.revealContainer.classList.remove('is-active');
      void this.revealContainer.offsetHeight;
      this.revealContainer.classList.add('is-active');
    }

    renderCurrentState() {
      if (!this.currentState || !this.refreshDomReferences()) return;

      this.setDisplayState(this.currentState.isPending, this.currentState.hasDiscount);
      this.updatePriceMarkup(this.currentState.originalUnitPrice, this.currentState.finalUnitPrice, {
        hasDiscount: this.currentState.hasDiscount,
        isPending: this.currentState.isPending,
        animate: this.currentState.animate,
      });
      this.updateRevealMeta(this.currentState);
    }

    async resolveDiscount() {
      if (!this.refreshDomReferences()) return;

      const variantId = this.getVariantId();
      const nativeState = this.buildFallbackState();
      const runId = ++this.runId;

      this.currentState = {
        ...nativeState,
        isPending: Boolean(DISCOUNT_CODE),
        animate: false,
      };
      this.renderCurrentState();

      const resolvedStatePromise = (async () => {
        return resolveDiscountStateForVariant(variantId, nativeState.originalUnitPrice);
      })().catch(() => nativeState);

      const [resolvedState] = await Promise.all([
        resolvedStatePromise,
        new Promise((resolve) => window.setTimeout(resolve, REVEAL_DELAY_MS)),
      ]);

      if (runId !== this.runId) return;
      if (!this.refreshDomReferences()) return;

      this.currentState = {
        ...resolvedState,
        isPending: false,
        animate: resolvedState.hasDiscount,
      };
      this.renderCurrentState();

      if (resolvedState.hasDiscount) {
        createCelebrationBurst(this.celebrationHost);
      }
    }
  }

  class DrawerCelebrationController {
    constructor() {
      this.pendingVariantId = null;
      this.drawer = document.querySelector('cart-drawer');

      if (!this.drawer) return;

      this.observeDrawer();
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (!event || event.source === 'cart-items') return;
        this.queue(event.productVariantId || event.variantId || null);
      });
    }

    observeDrawer() {
      this.observer = new MutationObserver(() => {
        if (!this.drawer.classList.contains('active')) return;
        this.tryCelebrate();
      });

      this.observer.observe(this.drawer, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    queue(variantId) {
      this.pendingVariantId = variantId ? String(variantId) : null;
      window.setTimeout(() => this.tryCelebrate(), 250);
    }

    tryCelebrate() {
      if (!this.drawer?.classList.contains('active')) return;

      const selector = this.pendingVariantId
        ? `.cart-item[data-variant-id="${this.pendingVariantId}"][data-discounted="true"] .cart-item__prices-wrapper`
        : null;
      const lineTarget = selector ? this.drawer.querySelector(selector) : null;
      const footerTarget = this.drawer.querySelector('.cart-drawer__footer-totals[data-has-savings="true"] .cart-drawer__celebration');
      const target = lineTarget || footerTarget;

      if (!target) return;

      createCelebrationBurst(target);
      this.pendingVariantId = null;
    }
  }

  function initializeDiscountReveals() {
    document.querySelectorAll('product-info[id^="MainProduct-"]').forEach((root) => {
      if (root.anandDiscountRevealInstance) {
        root.anandDiscountRevealInstance.destroy();
      }
      root.anandDiscountRevealInstance = new PDPDiscountReveal(root);
    });

    if (!window.anandDrawerCelebrationController) {
      window.anandDrawerCelebrationController = new DrawerCelebrationController();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeDiscountReveals);
  } else {
    initializeDiscountReveals();
  }

  document.addEventListener('shopify:section:load', initializeDiscountReveals);

  window.AnandDiscountReveal = {
    queueDrawerCelebration(variantId) {
      window.anandDrawerCelebrationController?.queue(variantId);
    },
    refresh() {
      initializeDiscountReveals();
    },
  };
})();
